// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// A spatial layer Khora added from the chat (add_spatial_layer), rendered as a TOGGLEABLE
// GeoJSONLayer in the Project Viewer under the "Dashboard layers" panel. The chatbot (Shell) and the
// viewer (Project Viewer) are separate React trees, so this Redux slice is the channel: the chat
// dispatches addLayer; an EsriSplitViewer effect syncs the slice → scene; the Sidebar toggles it.
// EVERY layer (any size) has an S3 `key` + presigned `url` and streams from S3 — one copy, no size
// special-case. `geojson` is only a no-url fallback, not the normal render path.
export interface ViewerLayer {
  id: string;
  title: string;
  color?: string;
  url?: string; // presigned S3 GeoJSON URL — the render path for EVERY layer (any size); EXPIRES
  key?: string; // the durable S3 key behind the layer (every layer has one) — what makes it shareable
  geojson?: GeoJSON.FeatureCollection; // fallback ONLY when there's no url (presign miss / no-bucket dev)
  popupFields?: string[] | null; // attributes to show in each feature's popup (null = all)
  hasChart?: boolean; // whether features carry a per-feature `khora_chart` for the popup
  visible: boolean;
  // Org-broadcast share marker (Khora's ONE sharing mechanism): set to the gdb id this layer is
  // shared under — org members viewing that gdb receive it automatically via gdb-shared-pins.
  sharedGdbId?: string;
}

interface IViewerLayersState {
  layers: ViewerLayer[];
}

const initialState: IViewerLayersState = { layers: [] };

const viewerLayersSlice = createSlice({
  name: 'viewerLayers',
  initialState,
  reducers: {
    addLayer: (state, action: PayloadAction<ViewerLayer>) => {
      // Dedupe by id: a re-added layer (same label) REPLACES the previous entry in place, so a
      // verify-pass re-emit or a repeat request never stacks duplicate scene layers.
      const index = state.layers.findIndex((layer) => layer.id === action.payload.id);
      if (index >= 0) {
        state.layers[index] = action.payload;
      } else {
        state.layers.push(action.payload);
      }
    },
    setLayerVisible: (state, action: PayloadAction<{ id: string; visible: boolean }>) => {
      const layer = state.layers.find((entry) => entry.id === action.payload.id);
      if (layer) layer.visible = action.payload.visible;
    },
    setLayerShared: (state, action: PayloadAction<{ id: string; sharedGdbId?: string }>) => {
      const layer = state.layers.find((entry) => entry.id === action.payload.id);
      if (layer) layer.sharedGdbId = action.payload.sharedGdbId;
    },
    removeLayer: (state, action: PayloadAction<string>) => {
      state.layers = state.layers.filter((layer) => layer.id !== action.payload);
    },
    clearLayers: (state) => {
      state.layers = [];
    },
  },
});

export const {
  addLayer, setLayerVisible, setLayerShared, removeLayer, clearLayers,
} = viewerLayersSlice.actions;
export const { reducer } = viewerLayersSlice;
export default reducer;
